import React, { useEffect, useId, useMemo, useState } from "react";

import {
  buildDimensionValueSuggestions,
  classifyDimensionName,
  createEmptyDimension,
  estimateStructureChange,
  fill,
  getStructureSnapshot,
  normalizeOptionDimensions,
  splitDimensionValues,
  toggleDimensionValue,
} from "../studioEditModel";
import { normalizeDimensionKey } from "../../../../utils/catalogColorCover";
import {
  Btn,
  Chip,
  ConfirmDialog,
  Drawer,
  Field,
  Icon,
  Notice,
  Segmented,
  Select,
  Swatch,
  TextInput,
} from "../StudioUi";

/**
 * Edits a local copy of the structure, so "Anulo" really discards and nothing
 * half-edited leaks into the main save bar. Saving goes through the same
 * sequence and endpoint as before (variants first, then PUT structure).
 */
const StructureEditorDrawer = ({ studio }) => {
  const {
    tt,
    workspace,
    structureForm,
    isStructureEditorOpen,
    setIsStructureEditorOpen,
    handleSaveStructure,
    isSavingStructure,
    colorRecords,
    productFamily,
  } = studio;
  const ids = useId();
  const [form, setForm] = useState(structureForm);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    if (isStructureEditorOpen) setForm(structureForm);
    // Only reset when the drawer opens, not on every background refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStructureEditorOpen]);

  const estimate = useMemo(() => (form ? estimateStructureChange(form, workspace) : null), [form, workspace]);
  if (!isStructureEditorOpen || !form) return null;

  const isVariant = form.productMode === "variant";
  const dimensions = normalizeOptionDimensions(form.optionDimensions);
  const presentKinds = new Set(form.optionDimensions.map((dimension) => classifyDimensionName(dimension.name)));
  const missingKinds = [
    // Stored names stay Albanian, like the rest of the live catalog.
    { kind: "color", name: "Ngjyra", label: tt("stColorOption") },
    { kind: "size", name: "Madhësia", label: tt("stSizeOption") },
  ].filter((entry) => !presentKinds.has(entry.kind));
  const hasChanges = getStructureSnapshot(form) !== getStructureSnapshot(structureForm);
  const manualIncomplete =
    isVariant && form.generationMode === "manual" && !(form.manualCombinations || []).length;
  const variantIncomplete = isVariant && !dimensions.length;

  const setField = (name, value) =>
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === "productMode" && value === "simple") next.generationMode = "manual";
      return next;
    });
  const setDimension = (index, key, value) =>
    setForm((current) => ({
      ...current,
      optionDimensions: current.optionDimensions.map((dimension, dimensionIndex) =>
        dimensionIndex === index ? { ...dimension, [key]: value } : dimension
      ),
    }));
  // A blank first row is replaced rather than left behind an added Ngjyra/Madhësia.
  const addDimension = (name = "") =>
    setForm((current) => {
      const existing = current.optionDimensions;
      const onlyBlank = existing.length === 1 && !existing[0].name.trim() && !existing[0].valuesText.trim();
      return {
        ...current,
        optionDimensions: onlyBlank ? [createEmptyDimension(name)] : [...existing, createEmptyDimension(name)],
      };
    });
  const toggleValue = (index, value) =>
    setForm((current) => ({
      ...current,
      optionDimensions: current.optionDimensions.map((dimension, dimensionIndex) =>
        dimensionIndex === index
          ? { ...dimension, valuesText: toggleDimensionValue(dimension.valuesText, value) }
          : dimension
      ),
    }));
  const removeDimension = (index) =>
    setForm((current) => {
      const nextDimensions = current.optionDimensions.filter((_dimension, dimensionIndex) => dimensionIndex !== index);
      return {
        ...current,
        optionDimensions: nextDimensions.length ? nextDimensions : [createEmptyDimension()],
        manualCombinations: (current.manualCombinations || []).map((combination) => ({
          optionValues: (combination.optionValues || []).filter((_value, valueIndex) => valueIndex !== index),
        })),
      };
    });
  const addCombination = () =>
    setForm((current) => ({
      ...current,
      manualCombinations: [
        ...(current.manualCombinations || []),
        { optionValues: normalizeOptionDimensions(current.optionDimensions).map(() => "") },
      ],
    }));
  const removeCombination = (rowIndex) =>
    setForm((current) => ({
      ...current,
      manualCombinations: current.manualCombinations.filter((_combination, index) => index !== rowIndex),
    }));
  const setCombinationValue = (rowIndex, optionIndex, value) =>
    setForm((current) => ({
      ...current,
      manualCombinations: current.manualCombinations.map((combination, index) => {
        if (index !== rowIndex) return combination;
        const optionValues = [...(combination.optionValues || [])];
        optionValues[optionIndex] = value;
        return { optionValues };
      }),
    }));

  const close = () => {
    setIsConfirmOpen(false);
    setIsStructureEditorOpen(false);
  };

  const confirmSave = async () => {
    const ok = await handleSaveStructure(form);
    setIsConfirmOpen(false);
    if (ok) setIsStructureEditorOpen(false);
  };

  return (
    <>
      <Drawer
        isOpen
        onClose={close}
        title={tt("stTitle")}
        description={tt("stCopy")}
        icon="variants"
        size="lg"
        closeLabel={tt("close")}
        footer={
          <>
            <Btn variant="ghost" onClick={close}>
              {tt("cancel")}
            </Btn>
            <Btn
              variant="primary"
              icon="check"
              disabled={!hasChanges || isSavingStructure || variantIncomplete || manualIncomplete}
              onClick={() => setIsConfirmOpen(true)}
            >
              {isSavingStructure ? tt("saving") : tt("stSave")}
            </Btn>
          </>
        }
      >
        <section className="pse-drawer-section">
          <h3 className="pse-drawer-section__title">{tt("stHasVariants")}</h3>
          <Segmented
            ariaLabel={tt("stHasVariants")}
            value={form.productMode}
            onChange={(value) => setField("productMode", value)}
            options={[
              { value: "variant", label: tt("stYes"), icon: "variants" },
              { value: "simple", label: tt("stNo"), icon: "cube" },
            ]}
          />
        </section>

        {isVariant ? (
          <section className="pse-drawer-section">
            <h3 className="pse-drawer-section__title">{tt("stOptions")}</h3>
            <div className="pse-dimension-list">
              {form.optionDimensions.map((dimension, index) => {
                const values = splitDimensionValues(dimension.valuesText);
                const selectedKeys = new Set(values.map(normalizeDimensionKey));
                const kind = classifyDimensionName(dimension.name);
                const suggestions = buildDimensionValueSuggestions({
                  name: dimension.name,
                  productFamily,
                  colorRecords,
                  currentValues: values,
                });
                return (
                  <div key={`dimension-${index}`} className="pse-dimension">
                    <div className="pse-dimension__fields">
                      <Field label={tt("stOptionName")} htmlFor={`${ids}-dim-name-${index}`}>
                        <TextInput
                          id={`${ids}-dim-name-${index}`}
                          value={dimension.name}
                          placeholder={tt("stOptionNamePlaceholder")}
                          onChange={(value) => setDimension(index, "name", value)}
                        />
                      </Field>
                      <Field label={tt("stValues")} htmlFor={`${ids}-dim-values-${index}`} hint={tt("stValuesHint")}>
                        <TextInput
                          id={`${ids}-dim-values-${index}`}
                          value={dimension.valuesText}
                          placeholder={tt("stValuesPlaceholder")}
                          onChange={(value) => setDimension(index, "valuesText", value)}
                        />
                      </Field>
                    </div>
                    {suggestions.length ? (
                      <div className="pse-value-picker" role="group" aria-label={tt("stPickValues")}>
                        {suggestions.map((value) => {
                          const isSelected = selectedKeys.has(normalizeDimensionKey(value));
                          return (
                            <button
                              key={value}
                              type="button"
                              aria-pressed={isSelected}
                              className={`pse-value-picker__option${isSelected ? " is-selected" : ""}`}
                              onClick={() => toggleValue(index, value)}
                            >
                              {kind === "color" ? <Swatch value={value} colorRecords={colorRecords} size={12} /> : null}
                              {value}
                            </button>
                          );
                        })}
                      </div>
                    ) : values.length ? (
                      <div className="pse-chip-row">
                        {values.map((value, valueIndex) => (
                          <Chip key={`${value}-${valueIndex}`}>{value}</Chip>
                        ))}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="pse-icon-btn pse-dimension__remove"
                      aria-label={tt("stRemoveOption")}
                      title={tt("stRemoveOption")}
                      onClick={() => removeDimension(index)}
                    >
                      <Icon name="trash" size={17} />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="pse-dimension-add">
              {missingKinds.map((entry) => (
                <Btn key={entry.kind} variant="secondary" icon="plus" onClick={() => addDimension(entry.name)}>
                  {entry.label}
                </Btn>
              ))}
              <Btn variant="ghost" icon="plus" onClick={() => addDimension()}>
                {missingKinds.length ? tt("stAddOtherOption") : tt("stAddOption")}
              </Btn>
            </div>
          </section>
        ) : null}

        {isVariant && form.generationMode === "manual" ? (
          <section className="pse-drawer-section">
            <h3 className="pse-drawer-section__title">{tt("stCombinations")}</h3>
            {!(form.manualCombinations || []).length ? (
              <Notice tone="warning">{tt("stCombinationsEmpty")}</Notice>
            ) : null}
            {(form.manualCombinations || []).map((combination, rowIndex) => (
              <div key={`combination-${rowIndex}`} className="pse-combination">
                {dimensions.map((dimension, optionIndex) => (
                  <Select
                    key={`${dimension.name}-${optionIndex}`}
                    aria-label={dimension.name}
                    value={combination.optionValues?.[optionIndex] || ""}
                    onChange={(value) => setCombinationValue(rowIndex, optionIndex, value)}
                  >
                    <option value="">
                      {dimension.name}: {tt("stSelect")}
                    </option>
                    {dimension.values.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </Select>
                ))}
                <button
                  type="button"
                  className="pse-icon-btn"
                  aria-label={tt("remove")}
                  onClick={() => removeCombination(rowIndex)}
                >
                  <Icon name="trash" size={17} />
                </button>
              </div>
            ))}
            <Btn variant="secondary" icon="plus" onClick={addCombination} disabled={!dimensions.length}>
              {tt("stAddCombination")}
            </Btn>
          </section>
        ) : null}

        <details className="pse-disclosure">
          <summary>
            <Icon name="chevronRight" size={16} className="pse-disclosure__chevron" />
            {tt("stAdvanced")}
          </summary>
          <div className="pse-disclosure__body">
            <Field label={tt("stGeneration")} htmlFor={`${ids}-generation`}>
              <Select
                id={`${ids}-generation`}
                value={form.generationMode}
                disabled={!isVariant}
                onChange={(value) => setField("generationMode", value)}
              >
                <option value="cartesian">{tt("stGenCartesian")}</option>
                <option value="manual">{tt("stGenManual")}</option>
              </Select>
            </Field>
            <Field label={tt("stInventory")} htmlFor={`${ids}-inventory`} hint={tt("stInventoryWarn")}>
              <Select
                id={`${ids}-inventory`}
                value={form.inventoryMode}
                onChange={(value) => setField("inventoryMode", value)}
              >
                <option value="aggregate">{tt("ovInventoryAggregate")}</option>
                <option value="serialized">{tt("ovInventorySerialized")}</option>
              </Select>
            </Field>
          </div>
        </details>

        {hasChanges && estimate ? (
          <section className="pse-drawer-section">
            <h3 className="pse-drawer-section__title">{tt("stPreview")}</h3>
            <ul className="pse-impact-list">
              {estimate.created + estimate.restored > 0 ? (
                <li className="is-positive">
                  <Icon name="plus" size={16} />
                  {fill(tt("stPreviewNew"), { count: estimate.created + estimate.restored })}
                </li>
              ) : null}
              {estimate.archived > 0 ? (
                <li className="is-negative">
                  <Icon name="archive" size={16} />
                  {fill(tt("stPreviewArchived"), { count: estimate.archived })}
                </li>
              ) : null}
              <li>
                <Icon name="check" size={16} />
                {fill(tt("stPreviewKept"), { count: estimate.kept })}
              </li>
            </ul>
            <p className="pse-muted pse-small">{tt("stPreviewEstimate")}</p>
          </section>
        ) : null}
      </Drawer>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        title={tt("stConfirmTitle")}
        confirmLabel={isSavingStructure ? tt("saving") : tt("stConfirmAction")}
        cancelLabel={tt("cancel")}
        tone={estimate?.archived ? "danger" : "primary"}
        isBusy={isSavingStructure}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={confirmSave}
      >
        {estimate?.archived ? <p>{fill(tt("stConfirmArchive"), { count: estimate.archived })}</p> : null}
        <p>{tt("stConfirmCopy")}</p>
      </ConfirmDialog>
    </>
  );
};

export default StructureEditorDrawer;
